<script setup>
import { ref, onMounted, onUnmounted, defineProps } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Drawer from "primevue/drawer";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Select from "primevue/select";
import Tag from "primevue/tag";
import ToggleSwitch from "primevue/toggleswitch";
import { useToast } from "primevue/usetoast";
import { sftpAPI } from "../service/api.js";

const toast = useToast();

const clients = ref([]);
const loading = ref(false);
const showDialog = ref(false);
const editingId = ref(null);

// Pull Now state — keyed by client ID
const activeJobs = ref({});    // clientId → job object (updated by polling)
const pollingIntervals = {};   // clientId → intervalId (plain object, not reactive)

// History drawer state
const showHistoryDrawer = ref(false);
const historyJobs = ref([]);
const historyLoading = ref(false);

const emptyForm = () => ({
  name: "",
  customer_id: "",
  host: "",
  port: 22,
  username: "",
  auth_type: "key",
  secret_manager_ref: "",
  remote_path: "/",
  file_pattern: "",
  file_type: "payment",
  active: true,
});

const form = ref(emptyForm());

const props = defineProps({
  customers: {
    type: Array,
    default: () => [],
  },
});

const customerOptions = () =>
  (props.customers || []).map((c) => ({ label: c.name, value: c.id }));

const fileTypeOptions = [
  { label: "Payment", value: "payment" },
  { label: "Reconciliation", value: "reconciliation" },
  { label: "Vendor", value: "vendor" },
];

const authTypeOptions = [
  { label: "Key", value: "key" },
  { label: "Password", value: "password" },
];

const fetchClients = async () => {
  loading.value = true;
  try {
    const response = await sftpAPI.listClients();
    clients.value = response.data || [];
  } catch (e) {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to fetch SFTP clients", life: 3000 });
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  editingId.value = null;
  form.value = emptyForm();
  showDialog.value = true;
};

const openEdit = (client) => {
  editingId.value = client.id;
  form.value = {
    name: client.name,
    customer_id: client.customer_id,
    host: client.host,
    port: client.port,
    username: client.username,
    auth_type: client.auth_type,
    secret_manager_ref: client.secret_manager_ref,
    remote_path: client.remote_path,
    file_pattern: client.file_pattern,
    file_type: client.file_type,
    active: client.active,
  };
  showDialog.value = true;
};

const saveClient = async () => {
  try {
    if (editingId.value) {
      await sftpAPI.updateClient(editingId.value, form.value);
      toast.add({ severity: "success", summary: "Saved", detail: "SFTP client updated", life: 3000 });
    } else {
      await sftpAPI.createClient(form.value);
      toast.add({ severity: "success", summary: "Created", detail: "SFTP client created", life: 3000 });
    }
    showDialog.value = false;
    fetchClients();
  } catch (e) {
    toast.add({ severity: "error", summary: "Error", detail: e.message || "Save failed", life: 3000 });
  }
};

const toggleActive = async (client) => {
  try {
    await sftpAPI.updateClient(client.id, { ...client, active: client.active });
  } catch (e) {
    client.active = !client.active;
    toast.add({ severity: "error", summary: "Error", detail: "Failed to update active status", life: 3000 });
  }
};

const customerName = (customerId) => {
  const c = (props.customers || []).find((c) => c.id === customerId);
  return c ? c.name : customerId;
};

// --- Pull Now ---

const triggerPull = async (client) => {
  try {
    const res = await sftpAPI.triggerPull(client.id);
    const jobId = res.data.job_id;
    activeJobs.value[client.id] = { status: "pending", job_id: jobId };
    startPolling(client.id, jobId);
  } catch (e) {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to trigger pull", life: 3000 });
  }
};

const startPolling = (clientId, jobId) => {
  if (pollingIntervals[clientId]) clearInterval(pollingIntervals[clientId]);
  pollingIntervals[clientId] = setInterval(async () => {
    try {
      const res = await sftpAPI.getPullJob(jobId);
      activeJobs.value[clientId] = res.data;
      if (res.data.status === "completed" || res.data.status === "failed") {
        clearInterval(pollingIntervals[clientId]);
        delete pollingIntervals[clientId];
      }
    } catch {
      clearInterval(pollingIntervals[clientId]);
      delete pollingIntervals[clientId];
    }
  }, 3000);
};

const isPolling = (clientId) => !!pollingIntervals[clientId];

// --- History drawer ---

const openHistory = async (client) => {
  showHistoryDrawer.value = true;
  historyLoading.value = true;
  historyJobs.value = [];
  try {
    const res = await sftpAPI.listPullJobs(client.id);
    historyJobs.value = res.data || [];
  } catch {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to load job history", life: 3000 });
  } finally {
    historyLoading.value = false;
  }
};

// --- Helpers ---

const jobSeverity = (status) =>
  ({ pending: "secondary", running: "info", completed: "success", failed: "danger" }[status] ?? "secondary");

const formatDuration = (job) => {
  if (!job.started_at || !job.completed_at) return "—";
  const s = Math.round((new Date(job.completed_at) - new Date(job.started_at)) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

const formatDate = (d) => (d ? new Date(d).toLocaleString() : "—");

onMounted(fetchClients);
onUnmounted(() => Object.values(pollingIntervals).forEach(clearInterval));
</script>

<template>
  <div>
    <div class="flex justify-content-end mb-3">
      <Button label="Add Client" icon="pi pi-plus" @click="openCreate" />
    </div>

    <DataTable :value="clients" :loading="loading" stripedRows>
      <Column field="name" header="Name" />
      <Column header="Customer" :body="(row) => customerName(row.customer_id)" />
      <Column field="host" header="Host" />
      <Column field="file_type" header="File Type" />
      <Column header="Active">
        <template #body="{ data }">
          <ToggleSwitch v-model="data.active" @change="toggleActive(data)" />
        </template>
      </Column>
      <Column header="Last Pull">
        <template #body="{ data }">
          <div v-if="activeJobs[data.id]">
            <Tag
              :value="activeJobs[data.id].status"
              :severity="jobSeverity(activeJobs[data.id].status)"
            />
            <div
              v-if="activeJobs[data.id].status === 'completed'"
              class="text-sm text-color-secondary mt-1"
            >
              {{ activeJobs[data.id].files_downloaded ?? 0 }} downloaded,
              {{ activeJobs[data.id].files_processed ?? 0 }} processed
            </div>
            <div
              v-if="activeJobs[data.id].status === 'failed'"
              class="text-sm text-red-500 mt-1"
            >
              {{ activeJobs[data.id].error_message }}
            </div>
          </div>
        </template>
      </Column>
      <Column header="">
        <template #body="{ data }">
          <div class="flex gap-1">
            <Button
              icon="pi pi-download"
              text
              rounded
              :disabled="!data.active || isPolling(data.id)"
              :loading="isPolling(data.id)"
              @click="triggerPull(data)"
              v-tooltip.top="'Pull Now'"
            />
            <Button
              icon="pi pi-history"
              text
              rounded
              @click="openHistory(data)"
              v-tooltip.top="'Pull History'"
            />
            <Button icon="pi pi-pencil" text rounded @click="openEdit(data)" />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Edit / Create dialog -->
    <Dialog
      v-model:visible="showDialog"
      modal
      :header="editingId ? 'Edit SFTP Client' : 'Add SFTP Client'"
      :style="{ width: '36rem' }"
    >
      <div class="flex flex-column gap-3">
        <div>
          <label class="block mb-1">Name</label>
          <InputText v-model="form.name" class="w-full" />
        </div>
        <div>
          <label class="block mb-1">Customer</label>
          <Select
            v-model="form.customer_id"
            :options="customerOptions()"
            optionLabel="label"
            optionValue="value"
            placeholder="Select customer"
            class="w-full"
          />
        </div>
        <div>
          <label class="block mb-1">Host</label>
          <InputText v-model="form.host" class="w-full" />
        </div>
        <div>
          <label class="block mb-1">Port</label>
          <InputNumber v-model="form.port" :min="1" :max="65535" class="w-full" />
        </div>
        <div>
          <label class="block mb-1">Username</label>
          <InputText v-model="form.username" class="w-full" />
        </div>
        <div>
          <label class="block mb-1">Auth Type</label>
          <Select
            v-model="form.auth_type"
            :options="authTypeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
          />
        </div>
        <div>
          <label class="block mb-1">Secret Manager Reference</label>
          <InputText v-model="form.secret_manager_ref" class="w-full" placeholder="projects/.../secrets/.../versions/latest" />
        </div>
        <div>
          <label class="block mb-1">Remote Path</label>
          <InputText v-model="form.remote_path" class="w-full" />
        </div>
        <div>
          <label class="block mb-1">File Pattern (optional)</label>
          <InputText v-model="form.file_pattern" class="w-full" placeholder="*.csv" />
        </div>
        <div>
          <label class="block mb-1">File Type</label>
          <Select
            v-model="form.file_type"
            :options="fileTypeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
          />
        </div>
        <div class="flex align-items-center gap-2">
          <ToggleSwitch v-model="form.active" inputId="active-toggle" />
          <label for="active-toggle">Active</label>
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" text @click="showDialog = false" />
        <Button label="Save" @click="saveClient" />
      </template>
    </Dialog>

    <!-- Pull history drawer -->
    <Drawer
      v-model:visible="showHistoryDrawer"
      position="right"
      header="Pull History"
      :style="{ width: '52rem' }"
    >
      <DataTable :value="historyJobs" :loading="historyLoading" size="small" stripedRows>
        <Column header="Triggered By" field="triggered_by" />
        <Column header="Status">
          <template #body="{ data }">
            <Tag :value="data.status" :severity="jobSeverity(data.status)" />
          </template>
        </Column>
        <Column header="Downloaded" field="files_downloaded" />
        <Column header="Processed" field="files_processed" />
        <Column header="Started At">
          <template #body="{ data }">{{ formatDate(data.started_at) }}</template>
        </Column>
        <Column header="Duration">
          <template #body="{ data }">{{ formatDuration(data) }}</template>
        </Column>
        <Column header="Error">
          <template #body="{ data }">
            <span v-if="data.error_message" class="text-red-500 text-sm">
              {{ data.error_message }}
            </span>
          </template>
        </Column>
      </DataTable>
    </Drawer>
  </div>
</template>
